import { useCallback, useSyncExternalStore } from 'react';

const LAST_SEEN_RELEASE_KEY = 'terraindividual:release-notes:last-seen';
const DISMISSED_RELEASE_KEY = 'terraindividual:release-notes:dismissed';
const RELEASE_NOTES_STORAGE_EVENT = 'terraindividual:release-notes:storage';
const RELEASE_NOTES_OPEN_EVENT = 'terraindividual:release-notes:open';
const memoryStorage: Record<string, string> = {};

const readStorage = (key: string) => {
  if (typeof window === 'undefined') return '';

  try {
    return window.localStorage.getItem(key) ?? memoryStorage[key] ?? '';
  } catch {
    return memoryStorage[key] ?? '';
  }
};

const writeStorage = (key: string, value: string) => {
  if (typeof window === 'undefined') return;

  memoryStorage[key] = value;

  try {
    window.localStorage.setItem(key, value);
  } catch {
    // The in-memory fallback still keeps the state in sync for the current session.
  }

  window.dispatchEvent(new Event(RELEASE_NOTES_STORAGE_EVENT));
};

const subscribeToStorage = (onStoreChange: () => void) => {
  if (typeof window === 'undefined') return () => undefined;

  window.addEventListener('storage', onStoreChange);
  window.addEventListener(RELEASE_NOTES_STORAGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener('storage', onStoreChange);
    window.removeEventListener(RELEASE_NOTES_STORAGE_EVENT, onStoreChange);
  };
};

export const useReleaseNotesState = () => {
  const lastSeenReleaseId = useSyncExternalStore(
    subscribeToStorage,
    () => readStorage(LAST_SEEN_RELEASE_KEY),
    () => ''
  );
  const dismissedReleaseId = useSyncExternalStore(
    subscribeToStorage,
    () => readStorage(DISMISSED_RELEASE_KEY),
    () => ''
  );
  const markReleaseAsSeen = useCallback(
    (releaseId: string) => writeStorage(LAST_SEEN_RELEASE_KEY, releaseId),
    []
  );
  const dismissRelease = useCallback(
    (releaseId: string) => writeStorage(DISMISSED_RELEASE_KEY, releaseId),
    []
  );

  return {
    lastSeenReleaseId,
    dismissedReleaseId,
    markReleaseAsSeen,
    dismissRelease,
  };
};

export const requestReleaseNotesOpen = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(RELEASE_NOTES_OPEN_EVENT));
  }
};

export const subscribeToReleaseNotesOpen = (openReleaseNotes: () => void) => {
  if (typeof window === 'undefined') return () => undefined;

  window.addEventListener(RELEASE_NOTES_OPEN_EVENT, openReleaseNotes);
  return () => window.removeEventListener(RELEASE_NOTES_OPEN_EVENT, openReleaseNotes);
};
